/*
 * Copyright 2025 MadeByTatwa LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { existsSync } from 'fs';
import { join, resolve, isAbsolute } from 'path';
import { readJsonFile } from '../utils/fs.js';
import type {
  DetectorPlugin,
  OmniRunConfig,
} from '../types/index.js';
import { logger } from '../utils/logger.js';

/**
 * Plugin Loader
 *
 * Handles discovery, loading, and validation of OmniRun plugins
 */
export class PluginLoader {
  private loadedPlugins: Map<string, DetectorPlugin> = new Map();
  private config: OmniRunConfig | null = null;

  /**
   * Load configuration from .omnirunrc.json
   */
  async loadConfig(cwd: string): Promise<OmniRunConfig | null> {
    const configPath = join(cwd, '.omnirunrc.json');

    if (!existsSync(configPath)) {
      return null;
    }

    try {
      this.config = (await readJsonFile(configPath)) as OmniRunConfig;
      return this.config;
    } catch (error) {
      logger.warn(`Failed to load .omnirunrc.json: ${error instanceof Error ? error.message : String(error)}`);
      return null;
    }
  }

  /**
   * Discover and load all plugins
   */
  async loadPlugins(cwd: string): Promise<DetectorPlugin[]> {
    const config = await this.loadConfig(cwd);

    if (!config?.plugins) {
      return [];
    }

    const plugins: DetectorPlugin[] = [];

    for (const [pluginName, pluginConfig] of Object.entries(config.plugins)) {
      if (!pluginConfig.enabled) {
        continue;
      }

      try {
        const plugin = await this.loadPlugin(pluginName, cwd, config);
        plugins.push(plugin);
        this.loadedPlugins.set(pluginName, plugin);
      } catch (error) {
        logger.error(`Failed to load plugin "${pluginName}": ${error instanceof Error ? error.message : String(error)}`);
      }
    }

    return plugins;
  }

  /**
   * Load a single plugin
   */
  private async loadPlugin(
    pluginName: string,
    cwd: string,
    config: OmniRunConfig
  ): Promise<DetectorPlugin> {
    let plugin: DetectorPlugin;

    try {
      // Try loading as npm package
      plugin = await this.loadFromNpm(pluginName);
    } catch (npmError) {
      // Try loading from custom paths
      if (config.pluginPaths) {
        plugin = await this.loadFromPaths(pluginName, config.pluginPaths, cwd);
      } else {
        throw npmError;
      }
    }

    // Validate plugin
    this.validatePlugin(plugin);

    // Initialize plugin if it has an init hook
    if (plugin.init) {
      await plugin.init();
    }

    return plugin;
  }

  /**
   * Load plugin from npm package
   */
  private async loadFromNpm(pluginName: string): Promise<DetectorPlugin> {
    try {
      // Dynamic import for ES modules
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const module = await import(pluginName);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const plugin = module.default || module;

      if (!plugin || typeof plugin !== 'object') {
        throw new Error('Plugin must export a detector object');
      }

      return plugin as DetectorPlugin;
    } catch (error) {
      throw new Error(`Cannot load npm package "${pluginName}": ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Load plugin from custom paths
   */
  private async loadFromPaths(
    pluginName: string,
    paths: string[],
    cwd: string
  ): Promise<DetectorPlugin> {
    for (const searchPath of paths) {
      const fullPath = isAbsolute(searchPath)
        ? searchPath
        : resolve(cwd, searchPath);

      const pluginPath = join(fullPath, pluginName);

      if (existsSync(pluginPath) || existsSync(`${pluginPath}.js`)) {
        try {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const module = await import(pluginPath);
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          return (module.default || module) as DetectorPlugin;
        } catch (error) {
          continue; // Try next path
        }
      }
    }

    throw new Error(`Plugin "${pluginName}" not found in custom paths`);
  }

  /**
   * Validate plugin structure
   */
  private validatePlugin(plugin: DetectorPlugin): void {
    if (!plugin.name || typeof plugin.name !== 'string') {
      throw new Error('Plugin must have a "name" property');
    }

    if (!plugin.detect || typeof plugin.detect !== 'function') {
      throw new Error(`Plugin "${plugin.name}" must have a "detect" method`);
    }

    if (!plugin.metadata) {
      throw new Error(`Plugin "${plugin.name}" must have "metadata" property`);
    }

    if (!plugin.metadata.name || !plugin.metadata.version) {
      throw new Error(`Plugin "${plugin.name}" metadata must include name and version`);
    }

    // Validate OmniRun version compatibility if specified
    if (plugin.metadata.omnirunVersion) {
      // TODO: Implement semver version checking
      // For now, just log a warning
      logger.info(
        `Plugin "${plugin.name}" requires OmniRun ${plugin.metadata.omnirunVersion}`
      );
    }
  }

  /**
   * Cleanup all loaded plugins
   */
  async cleanup(): Promise<void> {
    for (const plugin of this.loadedPlugins.values()) {
      if (plugin.cleanup) {
        try {
          await plugin.cleanup();
        } catch (error) {
          logger.warn(`Failed to cleanup plugin "${plugin.name}": ${error instanceof Error ? error.message : String(error)}`);
        }
      }
    }

    this.loadedPlugins.clear();
  }

  /**
   * Get all loaded plugins
   */
  getLoadedPlugins(): DetectorPlugin[] {
    return Array.from(this.loadedPlugins.values());
  }

  /**
   * Get plugin by name
   */
  getPlugin(name: string): DetectorPlugin | undefined {
    return this.loadedPlugins.get(name);
  }
}

